import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import CubeButton, { CubeButtonEvents } from './CubeButton';
import UIAssetKeys from './UIAssetKeys';
import FontKeys from './FontKeys';
import gameData, { getAreasInRegion, getTotalRequiredShapes, getTotalRequiredShapesForArea, getFoundAreasInRegion, getFoundShapesInRegion, REGIONS, getTotalFoundShapes } from './gameData';
import Game, { GameEvents } from './scenes/Game';
import SoundAssetKeys from './SoundAssetKeys';

/**
 * A Region represents a single region in the game. This class should be extended.
 */
export default class Region extends Phaser.GameObjects.Container
{
    /**
     * Create the Region screen
     * @param {Game} scene the owner scene
     */
    constructor(scene, region)
    {
        super(scene);

        this.scene = scene;

        this.region = region;

        this.isLocked = true;

        this.scene.add.existing(this);
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        // send everything off screen and set alpha to 0
        this._sendTargetsOffScreen();
        this.setAlpha(0);

        this._currentState = RegionStates.ZOOMED_OUT;

        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();
        this.backButton.disableInteractive();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        this.setAlpha(1);
        this._sendTargetsOffScreen();

        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();
        this.backButton.disableInteractive();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {

    }

    /**
     * Animates the state out.
     */
    async leave()
    {

    }

    /**
     * Add a reference image to this state.
     * @param {string} key the unique key to use for this reference (in case you want to access it elsewhere)
     * @param {string} path the relative path to the reference image
     * @param {Boolean} enablePlacer whether to enable the image placer debug tool after adding the reference image
     */
    _addReference(key, path, enablePlacer = true)
    {
        ABCya.Debug.addScreenReference(this.scene, key, path, (image) =>
        {
            if (enablePlacer) ABCya.Debug.enableImagePlacerDebug(this.scene, this.scene.background, image);
        });
    }

    /**
     * Set the on/off screen positions. These are the start/end positions when the game object is animating in.
     * @param {Phaser.GameObjects.GameObject} target the game object to add the animation positions to
     * @param {{x:number, y:number}} offScreenPosition the offscreen position (the position the game object starts at when animating in)
     */
    _setAnimationPositions(target, offScreenPosition)
    {
        target.onScreenPosition = { x: target.x, y: target.y };
        target.offScreenPosition = offScreenPosition;
    }

    /**
     * Set the animation positions for all targets in the array. This places the offscreen position as target.y - this.scene.scale.height
     * (basically places the targets above the screen).
     * @param {{x:number, y:number}[]} targets the array of targets to set the on/off screen positions.
     */
    _setAnimationPositionsAll(targets)
    {
        targets.forEach((target) =>
        {
            this._setAnimationPositions(target, { x: target.x, y: target.y - this.scene.scale.height });
        })
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        this.informationContainer.setPosition(this.informationContainer.offScreenPosition.x, this.informationContainer.offScreenPosition.y);
        this.menuButton.scale = 0;
        this.progressButton.scale = 0;
        this.backButton.scale = 0;
        this.greatWorkPopup.setAlpha(0);
        this.regionUnlockedPopup.setAlpha(0);
    }

    /**
     * Sends all targets to their off screen positions.
     * @param {{offScreenPosition: {x:number, y:number}, onScreenPosition: {x:number, y:number}}[]} targets the array of targets to send to their offscreen position
     */
    _sendTargetsOffScreenAll(targets)
    {
        targets.forEach(target =>
        {
            target.setPosition(target.offScreenPosition.x, target.offScreenPosition.y);
        })
    }

    /**
     * Creates the menu cube button and adds needed listeners.
     * @param {string} topKey the top menu button image in UIAssetKeys
     * @param {string} leftKey the left menu button image in UIAssetKeys
     * @param {string} rightKey the right menu button image in UIAssetKeys
     */
    _createMenuButton(topKey, leftKey, rightKey)
    {
        this.menuButton = new CubeButton(this.scene, topKey, leftKey, rightKey);
        this.add(this.menuButton);
        this.menuButton.setPosition(82, 89);
        this.menuButton.on(CubeButtonEvents.CLICKED, () => this.scene.events.emit(GameEvents.SHOW_MENU));
    }

    /**
     * Creates the switch scene cube button and adds needed listeners.
     * @param {string} topKey the top switch scene button image in UIAssetKeys
     * @param {string} leftKey the left switch scene button image in UIAssetKeys
     * @param {string} rightKey the right switch scene button image in UIAssetKeys
     */
    _createProgressButton(topKey, leftKey, rightKey)
    {
        this.progressButton = new CubeButton(this.scene, topKey, leftKey, rightKey);
        this.add(this.progressButton);
        this.progressButton.setPosition(944, 685);
        this.progressButton.on(CubeButtonEvents.CLICKED, () => this.scene.events.emit(GameEvents.SHOW_MENU));
    }

    /**
     * Creates the back cube button and adds needed listeners.
     * @param {string} topKey the top back button image in UIAssetKeys
     * @param {string} leftKey the left back button image in UIAssetKeys
     * @param {string} rightKey the right back button image in UIAssetKeys
     */
    _createBackButton(topKey, leftKey, rightKey)
    {
        this.backButton = new CubeButton(this.scene, topKey, leftKey, rightKey);
        this.add(this.backButton);
        this.backButton.setPosition(82, 89);
        this.backButton.on(CubeButtonEvents.CLICKED, this._leaveZoomedInState, this);
    }

    /**
     * Creates the information container. This shows the number of areas found and number of shapes found in this region.
     * @param {string} backgroundKey the circle background key of the shape/area number (in the UIAssetKeys)
     * @param {string} backgroundAllFoundKey the circle background key of the shape/area number when all shapes/areas have been found (in the UIAssetKeys)
     * @param {number} normalColor the normal text color of the number within the circle (before all shapes/areas are found)
     * @param {number} allFoundColor the text color of the number within the circle after all shapes/areas are found
     * @param {number} textColor the text color of the "Areas/Shapes" text
     */
    _createInformation(backgroundKey, backgroundAllFoundKey, normalColor = 0xffffff, allFoundColor = 0x2c2c89, textColor = 0x5f5fd6)
    {
        this._areaCount = getAreasInRegion(this.region);
        this._shapesCount = getTotalRequiredShapes(this.region);
        this._normalColor = Phaser.Display.Color.IntegerToColor(normalColor).rgba;
        this._allFoundColor = Phaser.Display.Color.IntegerToColor(allFoundColor).rgba;
        this._backgroundKey = backgroundKey;
        this._backgroundAllFoundKey = backgroundAllFoundKey;

        this.add(this.informationContainer = this.scene.add.container());

        this.areasExploredCountBg = ABCya.Images.addImage(45, 650, backgroundKey, this.informationContainer);
        this.shapesFoundCountBg = ABCya.Images.addImage(45, 721, backgroundKey, this.informationContainer);

        this.areasExploredCountText = ABCya.Text.addText(45, 650, 16, normalColor, FontKeys._graystrokeregular, "0/" + this._areaCount, this.informationContainer);
        this.shapesFoundCountText = ABCya.Text.addText(45, 721, 16, normalColor, FontKeys._graystrokeregular, "0/" + this._shapesCount, this.informationContainer);

        this.areasExploredText = ABCya.Text.addText(82, 626, 32, textColor, FontKeys._retrigaregular, gameData.text.areasExplored, this.informationContainer, false);
        this.shapesFoundText = ABCya.Text.addText(82, 696, 30, textColor, FontKeys._retrigaregular, gameData.text.shapesFound, this.informationContainer, false);

        this._setAnimationPositions(this.informationContainer, { x: -175, y: this.informationContainer.y });
    }

    /**
     * Call this function to update all the game data associated with this region;
     * Override this function so that it updates all area data;
     * This can be used to make the game in sync with the actual game data (after a new game reset for example).
     * Should also be called before entering the region state.
     */
    updateRegionData()
    {
        let areasExplored = getFoundAreasInRegion(this.region);
        this.updateAreasExplored(areasExplored);

        this.updateShapesFound(getFoundShapesInRegion(this.region));
    }

    /**
     * Updates the number of areas explored and sets the graphics/text/colors as appropriate.
     * @param {number} areasExplored the number of areas explored
     */
    updateAreasExplored(areasExplored)
    {
        this.areasExploredCountText.text = areasExplored + "/" + this._areaCount;
        if (areasExplored == this._areaCount)
        {
            this.areasExploredCountBg.setFrame(this._backgroundAllFoundKey);
            this.areasExploredCountText.setColor(this._allFoundColor);
        }
        else
        {
            this.areasExploredCountBg.setFrame(this._backgroundKey);
            this.areasExploredCountText.setColor(this._normalColor);
        }
    }

    /**
     * Updates the number of shapes found and sets the graphics/text/colors as appropriate.
     * @param {number} shapesFound the number of shapes found
     */
    updateShapesFound(shapesFound)
    {
        this._shapesFound = shapesFound;
        this.shapesFoundCountText.text = this._shapesFound + "/" + this._shapesCount;
        if (shapesFound == this._shapesCount)
        {
            this.shapesFoundCountBg.setFrame(this._backgroundAllFoundKey);
            this.shapesFoundCountText.setColor(this._allFoundColor);
        }
        else
        {
            this.shapesFoundCountBg.setFrame(this._backgroundKey);
            this.shapesFoundCountText.setColor(this._normalColor);
        }
    }

    /**
     * Zooms into the desired area.
     * @param {string} area the AREAS enum value
     */
    _zoomIntoArea(area)
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._zoom_in, this.scene);
    }

    /**
     * Leave the zoomed in state/area. Override to animate out from an area.
     */
    _leaveZoomedInState()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._zoom_out, this.scene, 1, false, 0.7);
    }

    /**
     * Creates the great work popup, which is shown when all shapes in an area have been found.
     */
    _createGreatWorkPopup()
    {
        this.add(this.greatWorkPopup = this.scene.add.container());
        this.greatWorkPopup.add(this.scene.add.rectangle((this.scene.scale.width / 2), (this.scene.scale.height / 2), this.scene.scale.width, this.scene.scale.height, 0x306379, 0.5).setInteractive());
        ABCya.Images.addImage((this.scene.scale.width / 2), (this.scene.scale.height / 2), UIAssetKeys._Pop_ups_PopUp_GrtWrkBG, this.greatWorkPopup);
        this.greatWorkText = ABCya.Images.addImage((this.scene.scale.width / 2), (this.scene.scale.height / 2), UIAssetKeys._Pop_ups_PopUp_GrtWrk, this.greatWorkPopup);
    }

    /**
     * Creates the region unlocked popup, which is shown when a new region is unlocked.
     */
    _createRegionUnlockedPopup()
    {
        this.add(this.regionUnlockedPopup = this.scene.add.container());
        this.regionUnlockedPopup.add(this.scene.add.rectangle((this.scene.scale.width / 2), (this.scene.scale.height / 2), this.scene.scale.width, this.scene.scale.height, 0x306379, 0.5).setInteractive());
        this.unlockBg = ABCya.Images.addImage((this.scene.scale.width / 2), (this.scene.scale.height / 2), UIAssetKeys._Pop_ups_PopUp_UnlockBG, this.regionUnlockedPopup);
        this.regionUnlockedShackle = ABCya.Images.addImage((this.scene.scale.width / 2) + 14, 304, UIAssetKeys._Pop_ups_Lock_ShckleOpn, this.regionUnlockedPopup).setOrigin(0.85366, 0.5);
        this.regionUnlockedBody = ABCya.Images.addImage((this.scene.scale.width / 2), 337, UIAssetKeys._Pop_ups_Lock_Body, this.regionUnlockedPopup);
    }

    /**
     * 
     * @param {number} x the x position of the progress counter
     * @param {number} y the y position of the progress counter
     * @param {string} backgroundKey the background key for the progress counter (found in UIAssetKeys)
     * @param {string} backgroundKeyComplete the background key for the progress count after all shapes in that area have been found (found in UIAssetKeys)
     * @param {number} completeFontColor the font color when all shapes have been found (normal font color is always white/0xffffff)
     * @param {string} area the area for this progress counter; one of the AREAS[this.region] enum values.
     */
    _createAreaProgressCounter(x, y, backgroundKey, backgroundKeyComplete, completeFontColor, area)
    {
        let total = getTotalRequiredShapesForArea(this.region, area);
        let areaProgressContainer = this.scene.add.container();
        this.add(areaProgressContainer);

        areaProgressContainer.bg = ABCya.Images.addImage(x, y, backgroundKey, areaProgressContainer);
        areaProgressContainer.bgKey = backgroundKey;
        areaProgressContainer.bgCompleteKey = backgroundKeyComplete;
        areaProgressContainer.completeFontColor = completeFontColor;
        let progressText = ABCya.Text.addText(x, y, 16, 0xffffff, FontKeys._graystrokeregular, "0/" + total, areaProgressContainer);

        // create a function that can be called to update the progress of this progress counter
        areaProgressContainer.updateProgress = (count) => 
        {
            progressText.text = count + "/" + total;
            if (count == total)
            {
                areaProgressContainer.bg.setFrame(backgroundKeyComplete);
                progressText.setColor(Phaser.Display.Color.IntegerToColor(completeFontColor).rgba);
            }
            else
            {
                areaProgressContainer.bg.setFrame(backgroundKey);
                progressText.setColor("#ffffff");
            }
        };
        return areaProgressContainer;
    }

    /**
     * This is a convience function for areaProgressContainer.updateProgress. It will update the counter and set the appropriate graphics/text.
     * @param {Phaser.GameObjects.Container} areaProgressContainer a progress counter container created from _createAreaProgressCounter
     * @param {number} count the number of shapes found in the area
     */
    _updateAreaProgress(areaProgressContainer, count)
    {
        areaProgressContainer.updateProgress(count);
    }

    /**
     * Play the unlock region animation.
     */
    _playUnlockAnimation()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._unlock_new_scene, this.scene, 1, false, 0.25);
        this.regionUnlockedShackle.setPosition((this.scene.scale.width / 2) + 14, 304).setScale(1);

        this.regionUnlockedPopup.addAt(this.shapeParticles = this.scene.add.particles(ABCya.Images.DEFAULT_ATLAS, [
            {
                frame: [UIAssetKeys._Pop_ups_Prtcl_1, UIAssetKeys._Pop_ups_Prtcl_2, UIAssetKeys._Pop_ups_Prtcl_3],
                x: (this.scene.scale.width / 2),
                y: (this.scene.scale.height / 2),
                angle: { min: 0, max: 360 },
                speed: { min: 200, max: 400 },
                accelerationX: { min: -20, max: -1 },
                accelerationY: { min: -20, max: -1 },
                alpha: { start: 1, end: 0, ease: 'Linear' },
                lifespan: 3000,
                scale: { start: 0.5, end: 1, ease: 'Linear' },
                maxParticles: 60,
                frequency: 20
            },
        ]).pause(), 0);

        let tweens = [
            { targets: this.regionUnlockedPopup, duration: 300, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0, onComplete: () => this.shapeParticles.resume() },
            { targets: this.regionUnlockedShackle, duration: 350, props: { y: 308 }, ease: Phaser.Math.Easing.Quadratic.In, offset: 300 },
            { targets: this.regionUnlockedShackle, duration: 350, props: { y: 296 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 650 },
            { targets: this.regionUnlockedShackle, duration: 350, props: { scaleX: -1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 },
            { targets: this.regionUnlockedPopup, duration: 300, props: { alpha: 0 }, ease: Phaser.Math.Easing.Quadratic.In, offset: 3000 },
        ];

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.shapeParticles.destroy();
            }
        });
    }

    /**
     * Checks if the space region is locked, and if it is, checks to see if it should become unlocked.
     * If it should become unlocked, it will play the unlock region animation (via _playUnlockAnimation function).
     */
    _checkSpaceRegionLockStatus()
    {
        if (this.scene.spaceRegion.isLocked)
        {
            // check to see if the space region became unlocked
            let shapesFound = getFoundShapesInRegion(REGIONS.BEACH) + getFoundShapesInRegion(REGIONS.DOWNTOWN) + getFoundShapesInRegion(REGIONS.CAMP);
            let shapesRequired = getTotalRequiredShapes(REGIONS.BEACH) + getTotalRequiredShapes(REGIONS.DOWNTOWN) + getTotalRequiredShapes(REGIONS.CAMP);

            if (gameData.region_unlock_requirements[REGIONS.SPACE] <= getTotalFoundShapes())
            {
                // unlock the space region
                this.scene.spaceRegion.isLocked = false;
                this._playUnlockAnimation();
            }
        }
    }
}

/**
 * The innner states for a region state. Each region should also have its own set of inner states, for each of its areas.
 */
export const RegionStates = {
    ZOOMED_OUT: "ZoomedOut",
    ZOOMED_IN: "ZoomedIn",
}